import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useVoiceCall } from '../hooks/useVoiceCall';
import { getIceServers, clearIceServersCache } from '../utils/iceServers';
import { registerCallBannerClickListener } from '../utils/swMessage';
import { fetchActiveIncomingCall } from '../utils/callRecovery';
import { isGroupCallBusy, dispatchGroupSignal } from '../lib/groupCallBridge';
import { initGroupCallEngine } from '../lib/groupCallEngine';
import GroupCallModal from '../components/GroupCallModal';
import VoiceCallModal from '../components/VoiceCallModal';
import Toast from '../components/Toast';

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const [userId, setUserId] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastDuration, setToastDuration] = useState(3000);
  const { t } = useTranslation();

  const callFinishedListenersRef = useRef(new Set());
  const currentUserIdRef = useRef(null);
  const processedRecoveryCallIdsRef = useRef(new Set());
  const lastRecoveryCheckAtRef = useRef(0);
  const incomingChannelReadyPromiseRef = useRef(null);
  const isCheckingMissedRef = useRef(false);
  const onlineDebounceTimerRef = useRef(null);
  const pendingMissedRecheckRef = useRef(null); // [결함 A] 건너뛴 계기 보관
  const missedRecheckTimerRef = useRef(null); // [결함 A] 다시 확인 타이머(동시에 1개만)

  const showToast = useCallback((msg, duration = 3000) => {
    setToastDuration(duration);
    setToastMessage(msg);
  }, []);

  // [결함 ④ 5-7] 망 변경 새로고침 후 대기 중인 통화 종료 안내 토스트 1회 소비
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('moowoo_pending_call_toast');
      if (raw) {
        sessionStorage.removeItem('moowoo_pending_call_toast');
        const data = JSON.parse(raw);
        if (Date.now() - (data.savedAt || 0) <= 15000) {
          showToast(data.message || t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'), data.duration || 7500);
          console.log(`[통화 안내] 새로고침 후 안내 표시 — ${data.duration || 7500}ms, 문구: ${String(data.message || '').slice(0, 20)}`);
        } else {
          console.log('[통화 안내] 오래된 예약 폐기');
        }
      }
    } catch (e) {
      console.warn('[CallContext] 대기 토스트 소비 실패:', e);
    }
  }, [showToast, t]);

  const registerCallFinishedListener = useCallback((fn) => {
    if (typeof fn === 'function') {
      callFinishedListenersRef.current.add(fn);
    }
  }, []);

  const unregisterCallFinishedListener = useCallback((fn) => {
    if (typeof fn === 'function') {
      callFinishedListenersRef.current.delete(fn);
    }
  }, []);

  const handleCallFinished = useCallback(() => {
    callFinishedListenersRef.current.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        console.warn('[CallContext] callFinishedListener 실행 오류:', e);
      }
    });
  }, []);

  // 1. 사용자 ID 및 내 프로필 정보 획득/동기화 & TURN ICE 서버 선행 워밍
  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async (uid) => {
      if (!uid) {
        if (isMounted) setMyProfile(null);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name, nickname, avatar, name_display_choice')
          .eq('id', uid)
          .maybeSingle();
        if (error) {
          console.error('[CallContext] 내 프로필 조회 실패:', error);
        }
        if (isMounted && data) {
          setMyProfile(data);
        }
      } catch (e) {
        console.error('[CallContext] 내 프로필 조회 예외:', e);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      const initialUserId = session?.user?.id || null;
      if (currentUserIdRef.current !== initialUserId) {
        currentUserIdRef.current = initialUserId;
        setUserId(initialUserId);
        fetchProfile(initialUserId);
        if (initialUserId) {
          // 로그인 사용자 대상 TURN ICE 서버 사전 워밍 (수락 시 RTT 지연 및 오디오 권한 체인 단절 방지)
          getIceServers().catch(() => {});
        } else {
          clearIceServersCache();
        }
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      const newUserId = session?.user?.id || null;
      if (currentUserIdRef.current !== newUserId) {
        currentUserIdRef.current = newUserId;
        setUserId(newUserId);
        fetchProfile(newUserId);
        if (newUserId) {
          getIceServers().catch(() => {});
        } else {
          clearIceServersCache();
        }
      } else if (event === 'SIGNED_OUT') {
        clearIceServersCache();
      }
    });

    // 서비스 워커 배너 클릭 수신 리스너 등록 (모드 B 용: 앱 직접 window.focus)
    const unregisterBannerClick = registerCallBannerClickListener();

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
      unregisterBannerClick();
    };
  }, []);

  // 2. 통화 엔진 훅 연동
  const {
    callState,
    incomingKind,
    isAccepting,
    callerInfo,
    partner,
    elapsedSeconds,
    remoteAudioRef,
    startVoiceCall,
    acceptCall,
    declineCall,
    endCall,
    handleCallOffer,
    handleCallAnswer,
    handleCallIce,
    handleCallEnd,
    handleCallRecover,
    handleCallRecoverReady,
    handleCallDecline,
    handleCallBusy,
    handleCallPing,
    peerRecoveryRemainingSec,
    handleCallAcceptedElsewhere,
    handleCallDeclinedElsewhere,
    handleOfferResendRequest,
    handleIceRestartRequest,
    handleIceRestartOffer,
    handleIceRestartAnswer,
    handlePeerConnectionRebuildRequest,
    handlePeerConnectionRebuildOffer,
    handlePeerConnectionRebuildAnswer,
    hasRecoveredOfferSdp,
    startRecoveredIncomingCall,
    isMuted,
    peerMuted,
    toggleMute,
    handleCallMute,
    handleCallVideoState,
    isMicLocked,
    replaceAudioTrack,
    getCurrentMicLabel,
    micChangeNotice,
    cameraNotice,
    cameraOn,
    startCamera,
    stopCamera,
    switchCameraFacing,
    cameraDeviceCount,
    peerVideoCapable,
    getPeerVideoCapable,
    localVideoStream,
    remoteVideoStream,
    remoteVideoOn,
    cameraFacing,
    isNetworkReconnectingSelf,
    isNetworkReconnectingPeer,
    isPeerAudioUnstable,
    isReconnectedSuccess,
    handleUserSignalStatus,
    isAudioConnected,
  } = useVoiceCall({
    userId,
    myProfile,
    showToast,
    t,
    onCallFinished: handleCallFinished,
    incomingChannelReadyPromiseRef,
  });

  const callStateRef = useRef(callState);
  callStateRef.current = callState;
  const startRecoveredIncomingCallRef = useRef(startRecoveredIncomingCall);
  startRecoveredIncomingCallRef.current = startRecoveredIncomingCall;

  const handleCallOfferRef = useRef(handleCallOffer);
  handleCallOfferRef.current = handleCallOffer;
  const handleCallAnswerRef = useRef(handleCallAnswer);
  handleCallAnswerRef.current = handleCallAnswer;
  const handleCallIceRef = useRef(handleCallIce);
  handleCallIceRef.current = handleCallIce;
  const handleCallEndRef = useRef(handleCallEnd);
  handleCallEndRef.current = handleCallEnd;
  const handleCallDeclineRef = useRef(handleCallDecline);
  handleCallDeclineRef.current = handleCallDecline;
  const handleCallBusyRef = useRef(handleCallBusy);
  handleCallBusyRef.current = handleCallBusy;
  const handleCallPingRef = useRef(handleCallPing);
  handleCallPingRef.current = handleCallPing;
  const handleCallMuteRef = useRef(handleCallMute);
  handleCallMuteRef.current = handleCallMute;
  const handleCallVideoStateRef = useRef(handleCallVideoState);
  handleCallVideoStateRef.current = handleCallVideoState;
  const handleCallAcceptedElsewhereRef = useRef(handleCallAcceptedElsewhere);
  handleCallAcceptedElsewhereRef.current = handleCallAcceptedElsewhere;
  const handleCallDeclinedElsewhereRef = useRef(handleCallDeclinedElsewhere);
  handleCallDeclinedElsewhereRef.current = handleCallDeclinedElsewhere;
  const handleOfferResendRequestRef = useRef(handleOfferResendRequest);
  handleOfferResendRequestRef.current = handleOfferResendRequest;
  const handleIceRestartRequestRef = useRef(handleIceRestartRequest);
  handleIceRestartRequestRef.current = handleIceRestartRequest;
  const handleIceRestartOfferRef = useRef(handleIceRestartOffer);
  handleIceRestartOfferRef.current = handleIceRestartOffer;
  const handleIceRestartAnswerRef = useRef(handleIceRestartAnswer);
  handleIceRestartAnswerRef.current = handleIceRestartAnswer;
  const handlePeerConnectionRebuildRequestRef = useRef(handlePeerConnectionRebuildRequest);
  handlePeerConnectionRebuildRequestRef.current = handlePeerConnectionRebuildRequest;
  const handlePeerConnectionRebuildOfferRef = useRef(handlePeerConnectionRebuildOffer);
  handlePeerConnectionRebuildOfferRef.current = handlePeerConnectionRebuildOffer;
  const handlePeerConnectionRebuildAnswerRef = useRef(handlePeerConnectionRebuildAnswer);
  handlePeerConnectionRebuildAnswerRef.current = handlePeerConnectionRebuildAnswer;
  const handleCallRecoverRef = useRef(handleCallRecover);
  handleCallRecoverRef.current = handleCallRecover;
  const handleCallRecoverReadyRef = useRef(handleCallRecoverReady);
  handleCallRecoverReadyRef.current = handleCallRecoverReady;
  const handleUserSignalStatusRef = useRef(handleUserSignalStatus);
  handleUserSignalStatusRef.current = handleUserSignalStatus;

  // 3. 수신 통화 복구 트리거 함수 (단일 관문)
  const triggerCallRecovery = useCallback(async (currentUid) => {
    if (!currentUid) return false;
    if (callStateRef.current !== 'idle' || isGroupCallBusy()) return false;

    try {
      const activeCall = await fetchActiveIncomingCall(supabase, currentUid);
      if (!activeCall) return false;

      // 복구 중복 진입 방어 관문: 이미 처리한 통화 건이면 무시
      if (processedRecoveryCallIdsRef.current.has(activeCall.callId)) {
        return false;
      }

      // Set 크기 상한 20개 유지
      if (processedRecoveryCallIdsRef.current.size >= 20) {
        const firstItem = processedRecoveryCallIdsRef.current.values().next().value;
        processedRecoveryCallIdsRef.current.delete(firstItem);
      }
      processedRecoveryCallIdsRef.current.add(activeCall.callId);

      // idle 상태 재확인 후 복구 시작
      if (callStateRef.current === 'idle') {
        console.log('[CallContext] DB 수신 진행 통화 복구 시작:', activeCall);
        startRecoveredIncomingCallRef.current?.(activeCall);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[CallContext] 통화 복구 실행 중 오류:', err);
      throw err;
    }
  }, []);

  // 3-1. 수신 통로 재연결·온라인 복귀·화면 복귀 시 놓친 전화 DB 확인 함수 (결함 ⑩ 대응 단일 관문)
  const checkMissedCalls = useCallback(async (triggerSource) => {
    if (isCheckingMissedRef.current) {
      console.log('[CallContext] 놓친 전화 DB 확인 건너뜀 (사유: 진행중)');
      pendingMissedRecheckRef.current = triggerSource;
      return;
    }
    const now = Date.now();
    const elapsed = now - lastRecoveryCheckAtRef.current;
    if (triggerSource !== 'resubscribe' && elapsed < 3000) {
      console.log('[CallContext] 놓친 전화 DB 확인 건너뜀 (사유: 3초제한)');
      if (!missedRecheckTimerRef.current) {
        const remainingMs = Math.max(100, 3000 - elapsed + 100);
        console.log(`[CallContext] 놓친 전화 다시 확인 예약 — ${remainingMs}ms 후 (계기: ${triggerSource})`);
        missedRecheckTimerRef.current = setTimeout(() => {
          missedRecheckTimerRef.current = null;
          checkMissedCallsRef.current?.(`${triggerSource}_재확인`);
        }, remainingMs);
      }
      return;
    }

    console.log(`[CallContext] 놓친 전화 DB 확인 시작 (계기: ${triggerSource})`);
    isCheckingMissedRef.current = true;
    try {
      const recovered = await triggerCallRecovery(userId);
      lastRecoveryCheckAtRef.current = Date.now();
      console.log(`[CallContext] 놓친 전화 DB 확인 완료 (결과: ${recovered ? '복구함' : '없음'})`);
    } catch (error) {
      console.error('[CallContext] 놓친 전화 DB 확인 실패:', error);
    } finally {
      isCheckingMissedRef.current = false;
      if (pendingMissedRecheckRef.current) {
        const pendingSource = pendingMissedRecheckRef.current;
        pendingMissedRecheckRef.current = null;
        if (!missedRecheckTimerRef.current) {
          const postElapsed = Date.now() - lastRecoveryCheckAtRef.current;
          const remainingMs = Math.max(100, 3000 - postElapsed + 100);
          console.log(`[CallContext] 놓친 전화 다시 확인 예약 — ${remainingMs}ms 후 (계기: ${pendingSource})`);
          missedRecheckTimerRef.current = setTimeout(() => {
            missedRecheckTimerRef.current = null;
            checkMissedCallsRef.current?.(`${pendingSource}_재확인`);
          }, remainingMs);
        }
      }
    }
  }, [userId, triggerCallRecovery]);

  const checkMissedCallsRef = useRef(checkMissedCalls);
  checkMissedCallsRef.current = checkMissedCalls;

  // 4. 복구 실행 시점 연결 (1) userId 확보 직후 1회 (2) visibilitychange -> visible 시점 (3) online 이벤트 (2000ms 디바운스)
  useEffect(() => {
    if (!userId) return;

    // (1) 마운트 후 userId 확보 직후 1회 실행
    triggerCallRecovery(userId).catch(() => {});

    // (2) visibilitychange 전환 감지 (화면 복귀 시 놓친 전화 확인)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkMissedCallsRef.current?.('visible');
      }
    };

    // (3) [보조 계기] online 이벤트 감지 (2000ms 디바운스 후 놓친 전화 확인)
    const handleOnline = () => {
      if (onlineDebounceTimerRef.current) {
        clearTimeout(onlineDebounceTimerRef.current);
      }
      onlineDebounceTimerRef.current = setTimeout(() => {
        checkMissedCallsRef.current?.('online');
      }, 2000);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      if (onlineDebounceTimerRef.current) {
        clearTimeout(onlineDebounceTimerRef.current);
        onlineDebounceTimerRef.current = null;
      }
      if (missedRecheckTimerRef.current) {
        clearTimeout(missedRecheckTimerRef.current);
        missedRecheckTimerRef.current = null;
      }
    };
  }, [userId, triggerCallRecovery]);

  // [단체 통화] 단체 엔진 연결 (3-2 3호 — 숨김 스위치가 꺼져 있으면 동작 없음)
  useEffect(() => {
    if (!userId) return undefined;
    return initGroupCallEngine(userId, {
      isOneToOneBusy: () => callStateRef.current !== 'idle',
    });
  }, [userId]);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const incomingChannelRemovingPromiseRef = useRef(null);

  // 5. 사용자별 개인 채널(user-signal-${userId}) 상시 구독
  useEffect(() => {
    if (!userId) return;

    let isActive = true;
    let hadChannelIssue = false;
    let isCancelled = false;
    let channelInstance = null;

    const setupIncomingChannel = async () => {
      if (incomingChannelRemovingPromiseRef.current) {
        try {
          await incomingChannelRemovingPromiseRef.current;
        } catch {}
      }
      if (isCancelled) return;

      if (import.meta.env.DEV) {
        console.log(`[CallContext] 개인 시그널링 채널 구독 시작: user-signal-${userId}`);
      }

      let readyResolve;
      incomingChannelReadyPromiseRef.current = new Promise((resolve) => {
        readyResolve = resolve;
      });

      const channel = supabase.channel(`user-signal-${userId}`, { config: { private: true } })
        .on('broadcast', { event: 'call:offer' }, (e) => handleCallOfferRef.current?.(e))
        .on('broadcast', { event: 'call:answer' }, (e) => handleCallAnswerRef.current?.(e))
        .on('broadcast', { event: 'call:ice' }, (e) => handleCallIceRef.current?.(e))
        .on('broadcast', { event: 'call:end' }, (e) => handleCallEndRef.current?.(e))
        .on('broadcast', { event: 'call:decline' }, (e) => handleCallDeclineRef.current?.(e))
        .on('broadcast', { event: 'call:busy' }, (e) => handleCallBusyRef.current?.(e))
        .on('broadcast', { event: 'call:ping' }, (e) => handleCallPingRef.current?.(e))
        .on('broadcast', { event: 'call:mute' }, (e) => handleCallMuteRef.current?.(e))
        .on('broadcast', { event: 'call:video_state' }, (e) => handleCallVideoStateRef.current?.(e))
        .on('broadcast', { event: 'call:accepted_elsewhere' }, (e) => handleCallAcceptedElsewhereRef.current?.(e))
        .on('broadcast', { event: 'call:declined_elsewhere' }, (e) => handleCallDeclinedElsewhereRef.current?.(e))
        .on('broadcast', { event: 'call:offer_resend' }, (e) => handleOfferResendRequestRef.current?.(e))
        .on('broadcast', { event: 'ice-restart-request' }, (e) => handleIceRestartRequestRef.current?.(e))
        .on('broadcast', { event: 'ice-restart-offer' }, (e) => handleIceRestartOfferRef.current?.(e))
        .on('broadcast', { event: 'ice-restart-answer' }, (e) => handleIceRestartAnswerRef.current?.(e))
        .on('broadcast', { event: 'call:rebuild-request' }, (e) => handlePeerConnectionRebuildRequestRef.current?.(e))
        .on('broadcast', { event: 'call:rebuild-offer' }, (e) => handlePeerConnectionRebuildOfferRef.current?.(e))
        .on('broadcast', { event: 'call:rebuild-answer' }, (e) => handlePeerConnectionRebuildAnswerRef.current?.(e))
        .on('broadcast', { event: 'call:recover' }, (e) => handleCallRecoverRef.current?.(e))
        .on('broadcast', { event: 'call:recover-ready' }, (e) => handleCallRecoverReadyRef.current?.(e))
        .on('broadcast', { event: 'group:signal' }, (e) => dispatchGroupSignal(e))
        .subscribe((status) => {
          console.log('[CallContext] user-signal 상태:', status);
          handleUserSignalStatusRef.current?.(status);
          if (status === 'SUBSCRIBED') {
            readyResolve?.(true);
            if (!isActive) return;
            if (hadChannelIssue) {
              hadChannelIssue = false;
              checkMissedCallsRef.current?.('resubscribe');
            }
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            readyResolve?.(false);
            if (!isActive) return;
            hadChannelIssue = true;
          }
        });

      channelInstance = channel;
    };

    setupIncomingChannel();

    return () => {
      isActive = false;
      isCancelled = true;
      if (channelInstance) {
        if (import.meta.env.DEV) {
          console.log(`[CallContext] 개인 시그널링 채널 구독 해제: user-signal-${userId}`);
        }
        incomingChannelRemovingPromiseRef.current = Promise.race([
          supabase.removeChannel(channelInstance),
          new Promise((resolve) => setTimeout(resolve, 1500))
        ]).catch(() => {});
      }
    };
  }, [userId]);

  const value = useMemo(() => ({
    callState,
    incomingKind,
    callerInfo,
    elapsedSeconds,
    isMuted,
    peerMuted,
    startVoiceCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    registerCallFinishedListener,
    unregisterCallFinishedListener,
  }), [
    callState,
    incomingKind,
    callerInfo,
    elapsedSeconds,
    isMuted,
    peerMuted,
    startVoiceCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    registerCallFinishedListener,
    unregisterCallFinishedListener,
  ]);

  return (
    <CallContext.Provider value={value}>
      {children}

      {/* 전역 숨김 오디오 엘리먼트 (WebRTC 원격 오디오 재생용) */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        style={{ display: 'none' }}
      />

      {/* 전역 통화 모달 (어느 화면에서나 최상단 z-50에 표시) */}
      <VoiceCallModal
        callState={callState}
        incomingKind={incomingKind}
        isAccepting={isAccepting}
        callerInfo={callerInfo}
        partner={partner}
        elapsedSeconds={elapsedSeconds}
        remoteAudioRef={remoteAudioRef}
        isMuted={isMuted}
        peerMuted={peerMuted}
        onToggleMute={toggleMute}
        showToast={showToast}
        onAccept={acceptCall}
        onDecline={declineCall}
        onEnd={endCall}
        isMicLocked={isMicLocked}
        hasRecoveredOfferSdp={hasRecoveredOfferSdp}
        onReconnectAudio={replaceAudioTrack}
        getCurrentMicLabel={getCurrentMicLabel}
        micChangeNotice={micChangeNotice}
        cameraOn={cameraOn}
        onStartCamera={startCamera}
        onStopCamera={stopCamera}
        cameraNotice={cameraNotice}
        switchCameraFacing={switchCameraFacing}
        cameraDeviceCount={cameraDeviceCount}
        peerVideoCapable={peerVideoCapable}
        getPeerVideoCapable={getPeerVideoCapable}
        localVideoStream={localVideoStream}
        remoteVideoStream={remoteVideoStream}
        remoteVideoOn={remoteVideoOn}
        cameraFacing={cameraFacing}
        peerRecoveryRemainingSec={peerRecoveryRemainingSec}
        isNetworkReconnectingSelf={isNetworkReconnectingSelf}
        isNetworkReconnectingPeer={isNetworkReconnectingPeer}
        isPeerAudioUnstable={isPeerAudioUnstable}
        isReconnectedSuccess={isReconnectedSuccess}
        isAudioConnected={isAudioConnected}
      />
      {/* 단체 통화 화면 (4-2c-2 · 단체 엔진이 idle이면 아무것도 그리지 않음) */}
      <GroupCallModal myUserId={userId} showToast={showToast} />

      {/* 전역 통화 관련 토스트 */}
      <Toast
        message={toastMessage}
        duration={toastDuration}
        position="top"
        onClose={() => setToastMessage(null)}
      />
    </CallContext.Provider>
  );
}

export function useCall() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}
